import { useEffect, useState } from "react";
import api from "../lib/api";
import { Coins, Mail, CheckCircle2, AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";

const Stat = ({ label, value, icon: Icon, accent }) => (
  <div className="border border-[#F3EFE0]/10 p-6 bg-[#0A0A0A] hover:border-[#D4AF37]/30 transition-colors">
    <div className="flex justify-between items-start">
      <div>
        <div className="coin-tag mb-2">{label}</div>
        <div className={`font-display text-4xl ${accent ? "text-[#D4AF37]" : "text-[#F3EFE0]"}`}>{value}</div>
      </div>
      <Icon className="w-5 h-5 text-[#6B6A65]" strokeWidth={1.5} />
    </div>
  </div>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentInquiries, setRecentInquiries] = useState([]);

  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data));
    api.get("/admin/inquiries").then(({ data }) => setRecentInquiries(data.slice(0, 5)));
  }, []);

  return (
    <div>
      <div className="coin-tag mb-2">Overview</div>
      <h1 className="font-display text-4xl tracking-tight mb-10" data-testid="dashboard-heading">Dashboard</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
        <Stat label="Total Coins" value={stats?.total_coins ?? "—"} icon={Coins} />
        <Stat label="Available" value={stats?.available ?? "—"} icon={CheckCircle2} accent />
        <Stat label="Sold" value={stats?.sold ?? "—"} icon={Coins} />
        <Stat label="New Inquiries" value={stats?.inquiries_new ?? "—"} icon={Mail} accent />
      </div>

      <div className="border border-[#F3EFE0]/10 bg-[#0A0A0A]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#F3EFE0]/10">
          <div>
            <div className="coin-tag">Inquiries</div>
            <div className="font-display text-2xl mt-1">Recent Activity</div>
          </div>
          <Link to="/admin/inquiries" className="coin-tag text-[#A19D94] hover:text-[#D4AF37]" data-testid="dashboard-view-all-inquiries">View All →</Link>
        </div>
        {recentInquiries.length === 0 ? (
          <div className="px-6 py-12 text-center text-[#6B6A65]">
            <AlertCircle className="w-6 h-6 mx-auto mb-3" strokeWidth={1.5} />
            <p>No inquiries yet.</p>
          </div>
        ) : (
          <div className="divide-y divide-[#F3EFE0]/5">
            {recentInquiries.map((inq) => (
              <div key={inq.id} className="px-6 py-4 flex items-center justify-between hover:bg-[#121212] transition-colors">
                <div>
                  <div className="text-[#F3EFE0]">{inq.name} <span className="text-[#6B6A65]">·</span> <span className="text-[#A19D94] text-sm">{inq.coin_name}</span></div>
                  <div className="text-xs text-[#6B6A65] mt-1 font-mono">{inq.email}</div>
                </div>
                <div className={`coin-tag px-2 py-1 border ${
                  inq.status === "new" ? "border-[#D4AF37]/40 text-[#D4AF37]" :
                  inq.status === "contacted" ? "border-[#2B435C]/60 text-[#A19D94]" :
                  "border-[#F3EFE0]/10 text-[#6B6A65]"
                }`}>{inq.status}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-12 border border-[#F3EFE0]/10 p-8 bg-[#0A0A0A]">
        <div className="coin-tag mb-2">Quick Actions</div>
        <h3 className="font-display text-2xl mb-6">Manage the Collection</h3>
        <div className="flex flex-wrap gap-3">
          <Link to="/admin/coins/new" className="bg-[#D4AF37] text-black font-medium px-6 py-3 hover:bg-[#F1C40F] transition-colors" data-testid="dashboard-add-coin">+ Add New Coin</Link>
          <Link to="/admin/coins" className="border border-[#F3EFE0]/20 px-6 py-3 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all" data-testid="dashboard-manage-coins">Manage Coins</Link>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
